import { Truck, Settings, Wrench, Recycle, ShieldCheck, Award, FileCheck } from "lucide-react";
import PageHeader from "../components/pageHeader";
import { useTranslation } from "../../../i18n/useTranslation";

const CREDENTIALS = [
  { Icon: ShieldCheck, title: "Insurance", body: "Every delivery partner carries verified insurance coverage before work begins." },
  { Icon: Award, title: "Technician Certification", body: "Field technicians are certified to the standards each device category requires." },
  { Icon: FileCheck, title: "Data Handling", body: "Chain-of-custody and data wiping standards apply to every device that passes through our network." },
];

const LifecycleServices = () => {
  const { t } = useTranslation();
  const h = t("deviceworks.pageHeaders.lifecycleServices");
  return (
    <div>
      <PageHeader eyebrow={h.eyebrow} title={h.title} description={h.description} />

      <div className="max-w-5xl mx-auto px-5 py-16 flex flex-col gap-16">
        {/* Procurement & Deployment */}
        <section id="procurement" className="scroll-mt-24 grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
          <div className="md:col-span-1">
            <Truck size={32} strokeWidth={1.5} style={{ color: "var(--dw-accent)" }} />
            <h2 className="pt-3 text-2xl">Procurement &amp; Deployment</h2>
          </div>
          <div className="md:col-span-2">
            <p className="dw-mini">
              From sourcing the right devices to configuring and rolling them out across
              your sites, we coordinate the full deployment — so day one is a working
              fleet, not a pile of boxes.
            </p>
          </div>
        </section>

        {/* Ongoing Management */}
        <section id="ongoing-management" className="scroll-mt-24 grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
          <div className="md:col-span-1">
            <Settings size={32} strokeWidth={1.5} style={{ color: "var(--dw-accent)" }} />
            <h2 className="pt-3 text-2xl">Ongoing Management</h2>
          </div>
          <div className="md:col-span-2">
            <p className="dw-mini">
              Day-to-day fleet health, monitored continuously. Our differentiator here is
              deep <strong style={{ color: "var(--dw-text)" }}>MDM (Mobile Device Management) integration</strong> —
              policy enforcement, remote diagnostics, and usage visibility built into the
              same platform that tracks the rest of the lifecycle.
            </p>
          </div>
        </section>

        {/* Maintenance & Repair */}
        <section id="maintenance-repair" className="scroll-mt-24 grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
          <div className="md:col-span-1">
            <Wrench size={32} strokeWidth={1.5} style={{ color: "var(--dw-accent)" }} />
            <h2 className="pt-3 text-2xl">Maintenance &amp; Repair</h2>
          </div>
          <div className="md:col-span-2">
            <p className="dw-mini">
              When something breaks, speed matters. Through our field service network we
              target a <strong style={{ color: "var(--dw-text)" }}>3-day turnaround</strong>, compared
              to the roughly <strong style={{ color: "var(--dw-text)" }}>2-week</strong> turnaround typical
              of standard OEM depot repair.
            </p>
            <div className="dw-card inline-flex gap-8 mt-4">
              <div>
                <div className="dw-mono text-2xl" style={{ color: "var(--dw-accent)" }}>~3 days</div>
                <div className="dw-mini text-xs">DeviceWorks network</div>
              </div>
              <div>
                <div className="dw-mono text-2xl" style={{ color: "var(--dw-text-faint)" }}>~14 days</div>
                <div className="dw-mini text-xs">Typical OEM depot repair</div>
              </div>
            </div>
          </div>
        </section>

        {/* End of Life */}
        <section id="end-of-life" className="scroll-mt-24 grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
          <div className="md:col-span-1">
            <Recycle size={32} strokeWidth={1.5} style={{ color: "var(--dw-accent)" }} />
            <h2 className="pt-3 text-2xl">End of Life / Disposal</h2>
          </div>
          <div className="md:col-span-2">
            <p className="dw-mini">
              Secure data wiping, responsible recycling, and documentation for every
              device that's retired — closing the loop on the lifecycle we manage from
              day one.
            </p>
          </div>
        </section>
      </div>

      {/* credentials */}
      <div className="dw-panel-bg border-t py-16 px-5" style={{ borderColor: "var(--dw-line)" }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <div className="dw-eyebrow mb-2">Required of Every Partner</div>
            <h2>Credentials</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {CREDENTIALS.map(({ Icon, title, body }) => (
              <div key={title} className="dw-card text-center">
                <Icon className="mx-auto" size={28} strokeWidth={1.5} style={{ color: "var(--dw-accent)" }} />
                <h3 className="pt-3 text-base">{title}</h3>
                <p className="dw-mini pt-1 text-sm">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LifecycleServices;
